import { useMemo, useState } from "react";
import { useAuth } from "../lib/auth";

/**
 * Estimated AWS cost for the spiderspysense.com stack ONLY.
 *
 * This is a *deterministic estimate* built from the resources we know this one
 * stack runs (one EC2 host, its public IPs, its EBS volume, the deploy bucket,
 * the Route 53 zone and egress) priced at AWS ap-south-1 public on-demand list
 * prices. It never calls AWS and never touches the shared account's other
 * workloads, so it can't leak another team's spend — but it is an estimate, not
 * an invoice. Numbers below are the assumptions; tweak the egress input to
 * explore. See db deploy notes for the resource inventory.
 */

const REGION = "ap-south-1 (Mumbai)";
const LAUNCH = "2026-06-26"; // stack went live on a t3.medium
const RESIZE = "2026-07-15"; // right-sized t3.medium -> t3a.small (~55% cheaper)
const HOURS_PER_MONTH = 730; // AWS billing convention (365*24/12)
const EC2_RATE_BEFORE = 0.0448; // t3.medium $/hr, ap-south-1 (pre-resize)
const EC2_RATE_NOW = 0.0123; // t3a.small $/hr, ap-south-1 (current, on-demand)
const EC2_RATE_SAVINGS = 0.0088; // t3a.small $/hr, projected 1-yr no-upfront Compute Savings Plan (~28% off)
const DAYS_PER_MONTH = HOURS_PER_MONTH / 24; // ≈30.42, keeps daily×month consistent
const FREE_EGRESS_GB = 100; // AWS free public egress per month
const EGRESS_USD_PER_GB = 0.1093; // ap-south-1 first-tier data transfer out

type Cat = "compute" | "network" | "storage" | "dns";

interface Line {
  key: string;
  label: string;
  detail: string;
  cat: Cat;
  monthly: number;
}

const CAT_COLOR: Record<Cat, string> = {
  compute: "#7c9cff",
  network: "#4fd1c5",
  storage: "#f6ad55",
  dns: "#b794f4"
};

// Fixed resources for this stack, priced at list. Egress is added separately
// from the adjustable assumption below.
const BASE_LINES: Line[] = [
  {
    key: "ec2",
    label: "EC2 — t3a.small",
    detail: "1 instance, 24×7 · $0.0123/hr",
    cat: "compute",
    monthly: EC2_RATE_NOW * HOURS_PER_MONTH
  },
  {
    key: "ipv4",
    label: "Public IPv4 × 2",
    detail: "app + TURN Elastic IPs · $0.005/hr each",
    cat: "network",
    monthly: 2 * 0.005 * HOURS_PER_MONTH
  },
  {
    key: "ebs",
    label: "EBS gp3 — 30 GB",
    detail: "root volume · $0.0912/GB-mo",
    cat: "storage",
    monthly: 30 * 0.0912
  },
  {
    key: "s3",
    label: "S3 — deploy bucket",
    detail: "≈1 GB + requests",
    cat: "storage",
    monthly: 0.1
  },
  {
    key: "route53",
    label: "Route 53",
    detail: "1 hosted zone $0.50 + queries",
    cat: "dns",
    monthly: 0.7
  }
];

function usd(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(iso: string): string {
  // Format the UTC-midnight instant in UTC so the label matches the UTC-based
  // day math (avoids showing "Jun 25" for a UTC-negative viewer).
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function CostsPage() {
  const { me } = useAuth();
  const [egressGb, setEgressGb] = useState(30);

  const model = useMemo(() => {
    const billableEgress = Math.max(0, egressGb - FREE_EGRESS_GB);
    const egressLine: Line = {
      key: "egress",
      label: "Data transfer out",
      detail: `${egressGb} GB/mo est. · first ${FREE_EGRESS_GB} GB free · $${EGRESS_USD_PER_GB}/GB`,
      cat: "network",
      monthly: billableEgress * EGRESS_USD_PER_GB
    };
    const lines = [...BASE_LINES, egressLine].sort((a, b) => b.monthly - a.monthly);
    const monthly = lines.reduce((s, l) => s + l.monthly, 0);
    const daily = monthly / DAYS_PER_MONTH; // current run-rate/day (t3a.small)

    // Cost history spans two compute rates — t3.medium until the resize, then
    // t3a.small. Everything except EC2 stayed constant, so only the compute
    // slope changes; the cumulative line bends DOWN at the resize date.
    const ec2NowDaily = (EC2_RATE_NOW * HOURS_PER_MONTH) / DAYS_PER_MONTH;
    const otherDaily = daily - ec2NowDaily; // IPs+EBS+S3+R53+egress, per day
    const ec2BeforeDaily = (EC2_RATE_BEFORE * HOURS_PER_MONTH) / DAYS_PER_MONTH;
    const dailyBefore = otherDaily + ec2BeforeDaily; // per-day run-rate pre-resize

    // Fractional days live → accrued-to-date without the +1 day overcount.
    const startMs = new Date(`${LAUNCH}T00:00:00Z`).getTime();
    const resizeMs = new Date(`${RESIZE}T00:00:00Z`).getTime();
    const elapsedExact = Math.max(0, (Date.now() - startMs) / 86_400_000);
    const elapsedDays = Math.floor(elapsedExact);
    const daysBefore = Math.min(elapsedExact, Math.max(0, (resizeMs - startMs) / 86_400_000));
    const daysAfter = Math.max(0, elapsedExact - daysBefore);
    const accruedAtResize = dailyBefore * daysBefore;
    const accumulated = accruedAtResize + daily * daysAfter;
    const forecastDays = 30;
    const projectedTotal = accumulated + daily * forecastDays;
    const totalDays = elapsedExact + forecastDays;

    // Projected run-rate if we commit the planned 1-yr Compute Savings Plan —
    // only the EC2 compute rate drops; everything else is unchanged.
    const monthlyWithSavings =
      monthly - EC2_RATE_NOW * HOURS_PER_MONTH + EC2_RATE_SAVINGS * HOURS_PER_MONTH;
    const dailyWithSavings = monthlyWithSavings / DAYS_PER_MONTH;
    const projectedWithSavings = accumulated + dailyWithSavings * forecastDays;

    return {
      lines,
      monthly,
      monthlyWithSavings,
      daily,
      elapsedExact,
      elapsedDays,
      daysBefore,
      accruedAtResize,
      accumulated,
      forecastDays,
      projectedTotal,
      projectedWithSavings,
      totalDays
    };
  }, [egressGb]);

  if (!me?.isAdmin) {
    return (
      <div className="w-full">
        <div className="glass p-6 text-white/60">
          Cost details are restricted to administrators.
        </div>
      </div>
    );
  }

  const {
    lines,
    monthly,
    monthlyWithSavings,
    daily,
    elapsedExact,
    elapsedDays,
    daysBefore,
    accruedAtResize,
    accumulated,
    forecastDays,
    projectedTotal,
    projectedWithSavings,
    totalDays
  } = model;

  // ---- SVG cumulative-cost chart ----
  // Cost accrues at a constant daily rate, so cumulative cost is a straight
  // line — two points for the accrued segment, two for the forecast. No loops,
  // no chance of NaN coordinates.
  // The chart fills a flex panel via preserveAspectRatio meet; this aspect keeps
  // letterboxing modest on a typical dashboard panel.
  const W = 1000;
  const H = 440;
  const PAD = { l: 60, r: 18, t: 18, b: 36 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;
  const maxY = projectedTotal;
  const x = (day: number) => PAD.l + (plotW * day) / totalDays;
  const y = (val: number) => PAD.t + plotH - (plotH * val) / (maxY || 1);

  // Accrued cost is a two-segment line: steeper (t3.medium) up to the resize,
  // then shallower (t3a.small). The bend — and the much flatter forecast — is
  // the saving made visible.
  const actualPts = `${x(0)},${y(0)} ${x(daysBefore)},${y(accruedAtResize)} ${x(elapsedExact)},${y(accumulated)}`;
  const forecastPts = `${x(elapsedExact)},${y(accumulated)} ${x(totalDays)},${y(maxY)}`;
  const areaPath = `M ${x(0)},${y(0)} L ${x(daysBefore)},${y(accruedAtResize)} L ${x(elapsedExact)},${y(accumulated)} L ${x(elapsedExact)},${y(0)} Z`;
  // Projected forecast if the 1-yr Savings Plan is committed — shallower than the
  // on-demand forecast; the gap between the two is the additional saving.
  const forecastSavingsPts = `${x(elapsedExact)},${y(accumulated)} ${x(totalDays)},${y(projectedWithSavings)}`;

  const yTicks = 4;
  const gridVals = Array.from({ length: yTicks + 1 }, (_, i) => (maxY / yTicks) * i);

  return (
    <div className="w-full h-[calc(100vh-8rem)] min-h-[440px] flex flex-col gap-3">
      {/* Header */}
      <div className="shrink-0">
        <h1 className="text-xl font-bold text-white leading-tight">AWS cost — spiderspysense.com</h1>
        <p className="text-xs text-white/50">
          Estimated from this stack's resources at {REGION} on-demand list prices — a planning
          estimate, not your actual AWS bill.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
        <Card label="Daily run-rate" value={usd(daily)} sub="at current resources" accent="#4fd1c5" />
        <Card
          label={`Accumulated (${elapsedDays}d)`}
          value={usd(accumulated)}
          sub={`since ${shortDate(LAUNCH)}`}
          accent="#7c9cff"
        />
        <Card
          label="Projected total (30d)"
          value={usd(projectedTotal)}
          sub="accrued + 30-day forecast"
          accent="#f6ad55"
        />
        <Card
          label="Monthly run-rate"
          value={usd(monthly)}
          sub={`≈ 730 hrs · ${usd(monthlyWithSavings)} with 1-yr plan`}
          accent="#b794f4"
        />
      </div>

      {/* Main row: chart + breakdown fill the remaining height */}
      <div className="grid lg:grid-cols-5 gap-3 flex-1 min-h-0">
        {/* Chart */}
        <div className="glass p-3 lg:col-span-3 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-2 shrink-0">
            <div className="text-white text-sm font-medium">Cumulative cost</div>
            <div className="flex items-center gap-3 text-[11px] text-white/50">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-0.5 bg-accent" /> Accrued
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-0"
                  style={{ borderTop: "2px dashed rgba(255,255,255,0.5)" }}
                />
                Forecast
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-0" style={{ borderTop: "2px dashed #48bb78" }} />
                with Savings Plan
              </span>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full h-full"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label="Cumulative cost chart"
            >
              {/* gridlines + y labels */}
              {gridVals.map((v, i) => (
                <g key={i}>
                  <line
                    x1={PAD.l}
                    x2={W - PAD.r}
                    y1={y(v)}
                    y2={y(v)}
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth={1}
                  />
                  <text x={PAD.l - 8} y={y(v) + 4} textAnchor="end" fontSize={13} fill="rgba(255,255,255,0.4)">
                    {usd(v)}
                  </text>
                </g>
              ))}

              {/* area under accrued */}
              <path d={areaPath} fill="rgba(79,209,197,0.14)" />

              {/* accrued line */}
              <polyline points={actualPts} fill="none" stroke="var(--accent, #4fd1c5)" strokeWidth={2.5} />
              {/* forecast line (dashed) — on-demand */}
              <polyline
                points={forecastPts}
                fill="none"
                stroke="rgba(255,255,255,0.55)"
                strokeWidth={2}
                strokeDasharray="6 6"
              />
              {/* projected forecast WITH the planned 1-yr Savings Plan (lower) */}
              <polyline
                points={forecastSavingsPts}
                fill="none"
                stroke="#48bb78"
                strokeWidth={2}
                strokeDasharray="2 5"
              />
              <circle cx={x(totalDays)} cy={y(projectedWithSavings)} r={3} fill="#48bb78" />

              {/* resize knee — where the slope drops from t3.medium to t3a.small */}
              {daysBefore > 0.5 && daysBefore < totalDays && (
                <g>
                  <line
                    x1={x(daysBefore)}
                    x2={x(daysBefore)}
                    y1={PAD.t}
                    y2={PAD.t + plotH}
                    stroke="rgba(124,156,255,0.35)"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                  />
                  <circle cx={x(daysBefore)} cy={y(accruedAtResize)} r={3.5} fill="#7c9cff" />
                  <text
                    x={x(daysBefore)}
                    y={PAD.t + 12}
                    textAnchor="middle"
                    fontSize={12}
                    fill="rgba(124,156,255,0.85)"
                  >
                    resized → t3a.small
                  </text>
                </g>
              )}

              {/* today marker */}
              <line
                x1={x(elapsedExact)}
                x2={x(elapsedExact)}
                y1={PAD.t}
                y2={PAD.t + plotH}
                stroke="rgba(255,255,255,0.25)"
                strokeWidth={1}
              />
              <circle cx={x(elapsedExact)} cy={y(accumulated)} r={4} fill="var(--accent, #4fd1c5)" />

              {/* x labels */}
              <text x={PAD.l} y={H - 12} fontSize={13} fill="rgba(255,255,255,0.4)">
                {shortDate(LAUNCH)}
              </text>
              <text x={x(elapsedExact)} y={H - 12} textAnchor="middle" fontSize={13} fill="rgba(255,255,255,0.65)">
                today
              </text>
              <text x={W - PAD.r} y={H - 12} textAnchor="end" fontSize={13} fill="rgba(255,255,255,0.4)">
                {shortDate(addDays(LAUNCH, Math.round(totalDays)))}
              </text>
            </svg>
          </div>
        </div>

        {/* Breakdown + assumptions */}
        <div className="glass lg:col-span-2 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-white/50 text-left">
                <tr className="border-b border-white/10">
                  <th className="px-3 py-2 font-normal">Resource</th>
                  <th className="px-3 py-2 text-right font-normal">Daily</th>
                  <th className="px-3 py-2 text-right font-normal">Monthly</th>
                  <th className="px-3 py-2 text-right font-normal">Share</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key} className="border-b border-white/5">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: CAT_COLOR[l.cat] }}
                        />
                        <div className="min-w-0">
                          <div className="text-white leading-tight">{l.label}</div>
                          <div className="text-white/40 text-[11px] leading-tight truncate">{l.detail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-white/70 tabular-nums">
                      {usd(l.monthly / DAYS_PER_MONTH)}
                    </td>
                    <td className="px-3 py-2 text-right text-white tabular-nums">{usd(l.monthly)}</td>
                    <td className="px-3 py-2 text-right text-white/50 tabular-nums">
                      {monthly ? `${Math.round((l.monthly / monthly) * 100)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-white/10 font-medium">
                  <td className="px-3 py-2 text-white">Total</td>
                  <td className="px-3 py-2 text-right text-white tabular-nums">{usd(daily)}</td>
                  <td className="px-3 py-2 text-right text-white tabular-nums">{usd(monthly)}</td>
                  <td className="px-3 py-2 text-right text-white/50">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="shrink-0 border-t border-white/10 p-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2 shrink-0">
              <label className="text-white/60 text-xs whitespace-nowrap">Egress GB/mo</label>
              <input
                type="number"
                min={0}
                value={egressGb}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setEgressGb(Number.isFinite(v) ? Math.min(1_000_000, Math.max(0, v)) : 0);
                }}
                className="w-24 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <p className="text-white/40 text-[11px] leading-snug flex-1 min-w-[180px]">
              First {FREE_EGRESS_GB} GB/mo free, then ${EGRESS_USD_PER_GB}/GB. List-price estimate at{" "}
              {REGION}; no AWS API call. Accrued reflects t3.medium until {shortDate(RESIZE)} and
              t3a.small after; forecast +{forecastDays}d. Green line = projected 1-yr no-upfront Compute
              Savings Plan (~28% off compute); free-tier credits not modelled.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({
  label,
  value,
  sub,
  accent
}: {
  label: string;
  value: string;
  sub: string;
  accent: string;
}) {
  return (
    <div className="glass py-2.5 pl-4 pr-3 relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
      <div className="text-white/50 text-[11px] leading-tight">{label}</div>
      <div className="text-white text-xl font-bold tabular-nums leading-tight">{value}</div>
      <div className="text-white/40 text-[11px] leading-tight mt-0.5">{sub}</div>
    </div>
  );
}
